/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { Tooltip } from 'radix-ui';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectCamera,
  selectCameraMode,
  selectFollowZoomStops,
  State,
  useTranslation,
} from '~/app';
import { cn } from '~/utils/styles';

import { setCameraMode, setFollowZoomStops } from './actions';
import { ReactComponent as ImgCamera } from './button-camera.svg';
import {
  FOLLOW_ZOOM_STOP_STEP,
  MAX_FOLLOW_ZOOM_STOPS,
  MAX_SCALE,
  MIN_FOLLOW_ZOOM_STOPS,
} from './cameraZoom';

export interface BoardCameraControlsProps {
  /** When in fixed mode, called for zoom-in button. Omit to disable zoom in when fixed. */
  onManualZoomIn?: () => void;
  /** When in fixed mode, called for zoom-out button. */
  onManualZoomOut?: () => void;
  /** When in fixed mode, zoom-in button is disabled (e.g. at max scale). */
  isManualZoomInDisabled?: boolean;
  /** When in fixed mode, zoom-out button is disabled (e.g. at min scale). */
  isManualZoomOutDisabled?: boolean;
  /** When true, layout is horizontal (e.g. when chat panel is open) so controls stay visible. */
  horizontal?: boolean;
}

/** Hand/pan icon for manual camera mode (24x24) */
const HandIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
    <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" />
    <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8" />
    <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
  </svg>
);

/** Zoom in (plus in magnifier) style 24x24 */
const ZoomInIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
    <path d="M11 8v6" />
    <path d="M8 11h6" />
  </svg>
);

/** Zoom out 24x24 */
const ZoomOutIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
    <path d="M8 11h6" />
  </svg>
);

export default function BoardCameraControls({
  onManualZoomIn,
  onManualZoomOut,
  isManualZoomInDisabled = false,
  isManualZoomOutDisabled = false,
  horizontal = false,
}: BoardCameraControlsProps): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cameraMode = useSelector((s: State) => selectCameraMode(s));
  const camera = useSelector((s: State) => selectCamera(s));
  const followZoomStops = useSelector((s: State) => selectFollowZoomStops(s));
  const isFollow = cameraMode === 'follow';

  // In follow mode: disable zoom-in when displayed scale is at max; disable zoom-out when
  // follow zoom stops are at min (displayed scale may plateau due to comfort cap, so we
  // must not use camera.scale alone for "at min").
  const atMinFollowZoom = isFollow ? followZoomStops <= MIN_FOLLOW_ZOOM_STOPS : false;
  const atMaxFollowZoom = isFollow
    ? camera.scale >= MAX_SCALE || followZoomStops >= MAX_FOLLOW_ZOOM_STOPS
    : false;
  const zoomInDisabled = isFollow ? atMaxFollowZoom : isManualZoomInDisabled;
  const zoomOutDisabled = isFollow ? atMinFollowZoom : isManualZoomOutDisabled;

  const zoomButtonBaseClass =
    'flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full shadow-lg focus:outline-none focus:ring-2 focus:ring-[#405820] focus:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed';
  const zoomButtonActiveClass =
    'bg-[#405820] text-white hover:bg-[#405820]/90 hover:shadow-xl disabled:opacity-60 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none disabled:hover:bg-gray-100 disabled:hover:shadow-none';

  const tooltipSide = horizontal ? ('bottom' as const) : ('left' as const);

  const handleZoomIn = () => {
    if (zoomInDisabled) return;
    if (isFollow) {
      const next = Math.min(MAX_FOLLOW_ZOOM_STOPS, followZoomStops + FOLLOW_ZOOM_STOP_STEP);
      if (next <= followZoomStops) return;
      dispatch(setFollowZoomStops(next));
    } else {
      onManualZoomIn?.();
    }
  };

  const handleZoomOut = () => {
    if (zoomOutDisabled) return;
    if (isFollow) {
      const next = Math.max(MIN_FOLLOW_ZOOM_STOPS, followZoomStops - FOLLOW_ZOOM_STOP_STEP);
      if (next >= followZoomStops) return;
      dispatch(setFollowZoomStops(next));
    } else {
      onManualZoomOut?.();
    }
  };

  const tooltipOffset = 6;
  return (
    <Tooltip.Provider delayDuration={0}>
      <div
        className={cn(
          'pointer-events-auto flex items-end gap-1 md:gap-1.5',
          horizontal ? 'flex-row' : 'w-9 flex-col md:w-10',
        )}
      >
        {/* Camera mode switch: vertical pill (default) or horizontal pill when horizontal */}
        <div
          className={cn(
            'flex overflow-hidden rounded-full bg-[#f0f2eb] shadow-lg focus-within:ring-2 focus-within:ring-[#405820] focus-within:ring-offset-2',
            horizontal ? 'h-9 flex-row md:h-10' : 'h-18 w-9 flex-col md:h-20 md:w-10',
          )}
          role="group"
          aria-label={t('camera_modeSwitch')}
        >
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                className={cn(
                  'flex items-center justify-center transition-colors focus:ring-0 focus:outline-none',
                  horizontal ? 'h-full min-w-9 md:min-w-10' : 'min-h-9 flex-1 md:min-h-10',
                  isFollow
                    ? 'bg-[#405820] text-white shadow-[inset_0_1px_3px_rgba(0,0,0,0.2)]'
                    : 'bg-[#f0f2eb] text-[#405820] hover:bg-[#e8ebe0]',
                )}
                onClick={() => dispatch(setCameraMode('follow'))}
                aria-label={t('camera_follow')}
                aria-pressed={isFollow}
              >
                <ImgCamera className="h-4 w-4 md:h-5 md:w-5" aria-hidden />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Content
              side={tooltipSide}
              sideOffset={tooltipOffset}
              className="rounded-lg border bg-white px-2 py-1 text-sm shadow-lg"
            >
              {t('camera_follow')}
            </Tooltip.Content>
          </Tooltip.Root>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                className={cn(
                  'flex items-center justify-center transition-colors focus:ring-0 focus:outline-none',
                  horizontal ? 'h-full min-w-9 md:min-w-10' : 'min-h-9 flex-1 md:min-h-10',
                  !isFollow
                    ? 'bg-[#405820] text-white shadow-[inset_0_1px_3px_rgba(0,0,0,0.2)]'
                    : 'bg-[#f0f2eb] text-[#405820] hover:bg-[#e8ebe0]',
                )}
                onClick={() => dispatch(setCameraMode('fixed'))}
                aria-label={t('camera_manual')}
                aria-pressed={!isFollow}
              >
                <HandIcon className="h-4 w-4 md:h-5 md:w-5" />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Content
              side={tooltipSide}
              sideOffset={tooltipOffset}
              className="rounded-lg border bg-white px-2 py-1 text-sm shadow-lg"
            >
              {t('camera_manual')}
            </Tooltip.Content>
          </Tooltip.Root>
        </div>

        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <button
              type="button"
              className={cn(zoomButtonBaseClass, zoomButtonActiveClass)}
              onClick={handleZoomIn}
              disabled={zoomInDisabled}
              aria-label={t('camera_zoomIn')}
            >
              <ZoomInIcon className="h-4 w-4 md:h-5 md:w-5" />
            </button>
          </Tooltip.Trigger>
          <Tooltip.Content
            side={tooltipSide}
            sideOffset={tooltipOffset}
            className="rounded-lg border bg-white px-2 py-1 text-sm shadow-lg"
          >
            {t('camera_zoomIn')}
          </Tooltip.Content>
        </Tooltip.Root>

        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <button
              type="button"
              className={cn(zoomButtonBaseClass, zoomButtonActiveClass)}
              onClick={handleZoomOut}
              disabled={zoomOutDisabled}
              aria-label={t('camera_zoomOut')}
            >
              <ZoomOutIcon className="h-4 w-4 md:h-5 md:w-5" />
            </button>
          </Tooltip.Trigger>
          <Tooltip.Content
            side={tooltipSide}
            sideOffset={tooltipOffset}
            className="rounded-lg border bg-white px-2 py-1 text-sm shadow-lg"
          >
            {t('camera_zoomOut')}
          </Tooltip.Content>
        </Tooltip.Root>
      </div>
    </Tooltip.Provider>
  );
}
